<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    currency: Currency
    pricePlan: PricePlan
    billingCycle?: BillingCycle
    isCurrent?: boolean
  }>(),
  {
    billingCycle: 'yearly',
    isCurrent: false,
  }
)

defineEmits<{
  choose: [pricePlan: PricePlan]
}>()

const { t } = useI18n()
const { isTrialing } = storeToRefs(useBusinessStore())

const billingCycleData = computed<BillingCycleData>(() => props.pricePlan[props.billingCycle])

const displayedPrice = computed(() => {
  // FIXME: Backend has inconsistent casing
  const lowercaseCurrency = props.currency.toLowerCase() as Lowercase<Currency>
  const seatPrice = billingCycleData.value[lowercaseCurrency].seat
  if (props.billingCycle === 'yearly') return `${props.currency} ${seatPrice / 12}`
  return `${props.currency} ${seatPrice}`
})

const planSubtitle = computed(() => {
  switch (props.pricePlan.name) {
    case 'Team':
      return t('business.plans.team.lead')
    case 'Pro':
      return t('business.plans.pro.lead')
    case 'Scale':
      return t('business.plans.scale.lead')
    default:
      return ''
  }
})

const features = computed<Partial<Record<PricePlanName, { text: string; footnote?: number; tooltip?: string }[]>>>(
  () => {
    return {
      Team: [
        {
          text: t('plan.features.aes_qes_included.text', {
            count: billingCycleData.value.signaturesIncluded.aesQes,
          }),
          footnote: 1,
          tooltip: t('plan.features.qualities_tooltip'),
        },
      ],
      Pro: [
        {
          text: t('plan.features.aes_qes_included.text', {
            count: billingCycleData.value.signaturesIncluded.aesQes,
          }),
          footnote: 1,
          tooltip: t('plan.features.qualities_tooltip'),
        },
      ],
    }
  }
)

const colorClass = computed(() => `plan-${props.pricePlan.name.toLowerCase()}`)
</script>

<template>
  <div class="plan" :class="{ 'plan--current': isCurrent }">
    <div :class="['plan__hue', `bg-${colorClass}`]" />
    <section class="pa-6">
      <div class="d-flex align-center justify-space-between mb-3">
        <div
          :data-cy="`price_plan_name_${pricePlan.name.toLowerCase()}`"
          :class="['text-h4 font-weight-bold', `text-${colorClass}`]"
        >
          {{ pricePlan.name }}
        </div>
        <div
          v-if="pricePlan.name === 'Pro'"
          class="rounded-pill bg-success text-body-2 text-uppercase font-weight-bold text-white px-2 py-1"
        >
          {{ $t('business.plan.most_popular') }}
        </div>
      </div>
      <div class="mb-5 text-body-2" data-subtitle>{{ planSubtitle }}</div>
      <div class="d-flex mb-6">
        <div
          class="text-h4 text-grey-darker font-weight-bold"
          style="white-space: nowrap"
          :data-cy="`price_plan_${pricePlan.name.toLowerCase()}_price`"
        >
          {{ displayedPrice }}
        </div>
        <div class="text-body-2 ml-2">
          {{ $t('billing.billing_cycle.per_month_per_seat') }}
          <br />
          {{ $t('billing.excl_vat') }}
        </div>
      </div>
      <v-btn v-if="isCurrent && !isTrialing" color="info" size="large" block disabled>{{
        $t('business.plan.current_plan')
      }}</v-btn>
      <v-btn
        v-else
        color="info"
        size="large"
        block
        :data-cy="`upgrade_${pricePlan.name.toLowerCase()}_button`"
        @click="$emit('choose', pricePlan)"
      >
        <i18n-t v-if="isTrialing && pricePlan.name === 'Pro'" keypath="business.plan.continue_with">
          <template #plan>{{ pricePlan.name }}</template>
        </i18n-t>
        <i18n-t v-else keypath="business.plan.choose">
          <template #plan>{{ pricePlan.name }}</template>
        </i18n-t>
      </v-btn>
      <v-divider class="mt-6 mb-3" />
      <div class="d-flex align-center">
        <template v-if="pricePlan.seatLimit === 1">
          <span
            ><strong>{{ billingCycleData.signaturesIncluded.ses }}</strong> {{ $t('business.plan.amount_ses') }}</span
          >
        </template>
        <template v-else>
          <span>
            <i18n-t keypath="business.plan.ses_unlimited.text">
              <template #highlight>
                <strong>{{ $t('business.plan.ses_unlimited.highlight') }}</strong>
              </template>
            </i18n-t>
          </span>
        </template>
      </div>
      <v-divider class="mt-3 mb-6" />
      <p v-for="(feature, index) in features[pricePlan.name] ?? []" :key="index">
        <span>{{ feature.text }}</span>
        <span v-if="feature.footnote" class="superscript font-weight-semibold">{{ feature.footnote }}</span>
        <v-tooltip v-if="feature.tooltip" location="right" content-class="bg-info" :open-on-click="true">
          <template #activator="{ props: activatorProps }">
            <v-icon class="ml-1" color="text" size="16" v-bind="activatorProps">custom:c_question</v-icon>
          </template>
          <span class="whitespace-pre-wrap">{{ feature.tooltip }}</span>
        </v-tooltip>
      </p>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.plan {
  width: 74 * $bw;
  box-shadow: 0px 9px 16px 1px rgba(40, 62, 102, 0.19);
  border-radius: $bw;
  overflow: hidden;
  background-color: $c-white;

  &--current {
    box-shadow: 0px 2px 5px rgba(40, 62, 102, 0.19);
  }

  &__hue {
    height: 4 * $bw;
  }

  [data-subtitle] {
    min-height: 10 * $bw;
  }
}

.superscript {
  position: relative;
  bottom: 0.375em;
  font-size: 0.75rem;
}
</style>
